import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mic, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { DictationRecorder } from './DictationRecorder';
import { VisitCardReview } from './VisitCardReview';
import { useToast } from '../../hooks/use-toast';
import { NewBadge } from '../NewBadge';
import { transcribeBlob } from '../voice/transcribe';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` } });

export const DictateVisitDialog = ({ open, onOpenChange, patientId, onDone }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState('');
  const [transcript, setTranscript] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [cards, setCards] = useState([]);
  const [adding, setAdding] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const reset = () => { setTranscript(''); setSessionId(null); setCards([]); setAdding(false); setBusy(''); };
  const fail = (e, title) => { setBusy(''); toast({ title, description: e.response?.data?.detail || 'Please try again', variant: 'destructive' }); };

  const parse = async (text) => {
    const full = transcript ? `${transcript}\n${text}` : text;
    setBusy('AI is building the Visit Card…');
    try {
      const r = await axios.post(`${BACKEND}/api/dictation/parse`, { transcript: full, patientId, sessionId, currentCards: cards }, auth());
      setTranscript(full);
      setSessionId(r.data.sessionId);
      setCards(r.data.cards);
      setAdding(false);
      setBusy('');
    } catch (e) { fail(e, 'Could not read dictation'); }
  };

  const onAudio = async (blob) => {
    setBusy('Transcribing…');
    try {
      const said = await transcribeBlob(blob, 'dictation');
      if (!said) { setBusy(''); toast({ title: 'Nothing heard', description: 'Please try again closer to the microphone.', variant: 'destructive' }); return; }
      await parse(said);
    } catch (e) { fail(e, 'Transcription failed'); }
  };

  const send = async (card) => {
    setSendingId(card.id);
    try {
      const r = await axios.post(`${BACKEND}/api/dictation/${sessionId}/send`, { card }, auth());
      setCards((cs) => cs.map((c) => (c.id === card.id ? { ...c, status: 'sent', results: r.data.results, pendingReferralId: r.data.pendingReferralId, sentName: r.data.patientName } : c)));
      toast({ title: 'Sent', description: `Everything for ${r.data.patientName} is on its way.` });
      onDone?.();
    } catch (e) { fail(e, 'Send failed'); }
    setSendingId(null);
  };

  const showRecorder = !cards.length || adding;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="dictate-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mic className="h-5 w-5 text-sky-600" /> Dictate Visit</DialogTitle>
        </DialogHeader>

        {showRecorder && (
          <>
            {adding && <p className="text-sm text-slate-600">Add or correct anything — AI will merge it with what you already said.</p>}
            <DictationRecorder onAudio={onAudio} onText={parse} busy={busy} />
          </>
        )}

        {cards.length > 0 && !adding && (
          <div className="space-y-4">
            <details className="text-sm text-slate-600" data-testid="dictation-transcript">
              <summary className="cursor-pointer font-medium">What you said</summary>
              <p className="mt-2 whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{transcript}</p>
            </details>
            {cards.map((c) => (
              <VisitCardReview key={c.id} card={c} sending={sendingId === c.id}
                onChange={(nc) => setCards((cs) => cs.map((x) => (x.id === nc.id ? nc : x)))} onSend={(final) => send(final || c)} />
            ))}
            {cards.some((c) => c.status !== 'sent') && (
              <Button variant="outline" onClick={() => setAdding(true)} data-testid="dictation-add-more-btn">
                <Plus className="h-4 w-4 mr-1" /> Add more / answer AI's questions
              </Button>
            )}
            {cards.every((c) => c.status === 'sent') && (
              <Button onClick={reset} data-testid="dictation-new-btn"><Mic className="h-4 w-4 mr-1" /> Dictate another visit</Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const DictateVisitBanner = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener('practcom:open-dictate', show);
    return () => window.removeEventListener('practcom:open-dictate', show);
  }, []);
  return (
    <>
      <div className="mb-4 rounded-xl bg-gradient-to-r from-sky-700 to-cyan-600 text-white p-4 flex flex-wrap items-center gap-4" data-testid="dictate-visit-banner">
        <div className="h-11 w-11 rounded-full bg-white/15 flex items-center justify-center"><Mic className="h-6 w-6" /></div>
        <div className="flex-1 min-w-[220px]">
          <p className="font-bold flex items-center gap-2">Dictate a visit <NewBadge testId="dictate-new-badge" /></p>
          <p className="text-sm text-white/85">Say who you saw and what you did. AI prepares education, post-op, consents, forms and Med Cards for one-tap Review &amp; Send.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-white text-sky-800 hover:bg-sky-50 font-semibold" data-testid="dictate-visit-open-btn">
          <Mic className="h-4 w-4 mr-1" /> Dictate Visit
        </Button>
      </div>
      <DictateVisitDialog open={open} onOpenChange={setOpen} onDone={onDone} />
    </>
  );
};

export default DictateVisitDialog;
